import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useBreakpoint } from './useBreakpoint';

export const KNOWN_NAV_HEIGHT = 64; // Hardcoded title bar height
export const KNOWN_COLLAPSED_FILTER_HEIGHT = 54; // Revised estimated collapsed filter height

// Helper function from useIsScrolledToTop
const isTop = () => window.scrollY < 5;

/**
 * Calculates the dynamic margin-top for main content based on header height
 * and scroll position on mobile.
 * On desktop, returns undefined.
 * On mobile:
 *  - Returns full measured header height when scrolled to top.
 *  - Returns collapsed header height (nav + estimated filter) when scrolled down.
 *
 * @param {boolean} [alwaysFullHeight=false] - If true, always returns the full height on mobile, ignoring scroll (e.g., for Calendar view).
 * @returns {{
 *  filterRowRef: React.RefObject<HTMLDivElement>,
 *  dynamicMarginTop: string | undefined,
 *  isAtTop: boolean,
 *  totalHeaderHeight: number,
 *  collapsedHeaderHeight: number
 * }}
 */
export function useDynamicHeaderMargin(alwaysFullHeight: boolean = false): {
  filterRowRef: React.RefObject<HTMLDivElement>;
  dynamicMarginTop: string | undefined;
  isAtTop: boolean;
  totalHeaderHeight: number;
  collapsedHeaderHeight: number;
} {
  const filterRowRef = useRef<HTMLDivElement>(null);
  const frameIdRef = useRef<number | null>(null); // Ref to store animation frame ID
  const [filterRowHeight, setFilterRowHeight] = useState(0);
  const [isAtTop, setIsAtTop] = useState(isTop()); // State from useIsScrolledToTop
  const { isMobile } = useBreakpoint();

  // Effect from useIsScrolledToTop
  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(isTop());
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const filterEl = filterRowRef.current;

    if (!filterEl) return; // Only need filterEl now

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const targetElement = entry.target as HTMLElement;

        if (frameIdRef.current) {
          cancelAnimationFrame(frameIdRef.current);
        }

        frameIdRef.current = requestAnimationFrame(() => {
          if (targetElement === filterEl) {
            setFilterRowHeight(targetElement.offsetHeight);
          }
        });
      }
    });

    observer.observe(filterEl);

    // Initial measurement
    setFilterRowHeight(filterEl.offsetHeight);

    return () => {
      observer.disconnect();
      // Cancel any pending animation frame on unmount
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, []); // Effect runs once on mount

  const totalHeaderHeight = KNOWN_NAV_HEIGHT + filterRowHeight;
  const collapsedHeaderHeight =
    KNOWN_NAV_HEIGHT + KNOWN_COLLAPSED_FILTER_HEIGHT;

  // Calculate final margin
  let marginTopValue: string | undefined = undefined;

  if (isMobile) {
    if (alwaysFullHeight) {
      marginTopValue = `${totalHeaderHeight}px`;
    } else {
      marginTopValue = isAtTop
        ? `${totalHeaderHeight}px`
        : `${collapsedHeaderHeight}px`;
    }
  }

  return {
    filterRowRef,
    dynamicMarginTop: marginTopValue,
    isAtTop,
    totalHeaderHeight,
    collapsedHeaderHeight,
  };
}
